import { useRouteParams, useRouter } from '@components/router';
import { CalendarPageBuilder, load, type Props } from './calendar-page-builder';
import { RuzcalMgmtPage } from '../mgmt-page';
import { SuccessModal } from './success-modal';
import { StepsHeader } from './steps-header';
import { BtnCopy, BtnPrimary, BtnSecondary } from '@components/buttons';
import { Case } from '@components/conditional';
import { IcoSettings } from '@components/icons';
import { useNagDialog } from './nag-dialog';
import { defCalRoute } from '../common';
import { ConfirmedModal } from './confirmed-modal';

export const route = defCalRoute({ authLevel: 'guide', load, Page });

function getPublicUrl(accountInfo: Props['state']['accountInfo']) {
  if (accountInfo.customDomain) {
    return `https://${accountInfo.customDomain}`;
  }
  return `${location.origin}/calendar/landing-page/${accountInfo.hostId}`;
}

function Page(props: Props) {
  const { modal } = useRouteParams();
  const router = useRouter();
  const modalParam = modal as 'confirmed' | 'welcome' | 'success' | undefined;
  const { accountInfo } = props.state;
  const isPublished = accountInfo.isPublished;
  const publicURL = getPublicUrl(props.state.accountInfo);

  useNagDialog({ isPublished, router });

  function closeModal() {
    const url = new URL(location.href);
    url.searchParams.delete('modal');
    router.rewrite(url.toString());
  }

  const content = (
    <>
      <StepsHeader
        showAuthMenu={!accountInfo.isPublished}
        currentStep={isPublished ? 'publish' : 'editpage'}
        ctaButtons={
          <Case
            when={isPublished}
            fallback={
              <BtnPrimary class="px-8 py-2.5 rounded-full" href="/calendar/signup">
                <span class="text-base mr-2">🚀</span>
                Publish & Get Your Link
              </BtnPrimary>
            }
          >
            <BtnSecondary class="pl-4 pr-6 py-2.5 rounded-full" href="/calendar/signup">
              <IcoSettings class="size-5 mr-2" />
              Configure Domain
            </BtnSecondary>
            <BtnCopy
              class="btn-primary pl-4 pr-6 py-3 rounded-full"
              copiedText="Link copied"
              value={publicURL}
              inset
            >
              Share Link
            </BtnCopy>
          </Case>
        }
      />
      {modalParam === 'success' && <SuccessModal hide={closeModal} />}
      {modalParam === 'confirmed' && <ConfirmedModal hide={closeModal} />}
      <CalendarPageBuilder {...props} publicURL={publicURL} />
    </>
  );

  // If the page is not published, we don't want to show the
  // calendar navigation wrapper.
  if (!accountInfo.isPublished) {
    return content;
  }

  return (
    <RuzcalMgmtPage title="Page Builder" currentPage="page-builder" hideHeader>
      {content}
    </RuzcalMgmtPage>
  );
}
